import { focusRingStyles } from '@beacon/ui/styles';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  indeterminate?: boolean;
}

export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, indeterminate = false, checked, ...rest }, ref) => {
    const effectiveChecked = indeterminate ? false : checked;

    return (
      <SwitchPrimitives.Root
        className={focusRingStyles({
          className: twMerge(
            [
              'border-ds-stroke-primary group/switch peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 shadow-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              'data-[state=unchecked]:bg-ds-surface-3',
              'data-[state=checked]:border-ds-accent-primary-9',
              'data-[state=checked]:bg-ds-accent-primary-9',
              'data-[indeterminate=true]:data-[state=unchecked]:border-ds-accent-primary-9',
              'data-[indeterminate=true]:data-[state=checked]:border-ds-accent-primary-9',
              'data-[indeterminate=true]:data-[state=unchecked]:bg-ds-accent-primary-9',
              'data-[indeterminate=true]:data-[state=checked]:bg-ds-accent-primary-9',
            ],
            className,
          ),
        })}
        {...rest}
        checked={effectiveChecked}
        data-indeterminate={indeterminate}
        ref={ref}
      >
        <SwitchPrimitives.Thumb
          className={twMerge([
            'bg-ds-surface-foundation pointer-events-none flex h-4 w-4 items-center justify-center rounded-full shadow-lg ring-0 transition',
            'data-[state=checked]:translate-x-4',
            'data-[state=unchecked]:translate-x-0',
            'group-data-[indeterminate=true]/switch:data-[state=checked]:translate-x-2',
            'group-data-[indeterminate=true]/switch:data-[state=unchecked]:translate-x-2',
            'group-data-[indeterminate=true]/switch:bg-transparent',
            'group-data-[indeterminate=true]/switch:shadow-none',
          ])}
        >
          <span
            className={twMerge([
              'bg-ds-surface-foundation h-1.5 w-4 rounded-full opacity-0',
              'group-data-[indeterminate=true]/switch:opacity-100',
            ])}
          />
        </SwitchPrimitives.Thumb>
      </SwitchPrimitives.Root>
    );
  },
);
Switch.displayName = SwitchPrimitives.Root.displayName;
